import { Suspense } from "react"

import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { RefetchIntervals } from "../../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { toDinero, toDineroOrZero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { DashboardCardSkeleton } from "../../../../dashboards/components/DashboardCardSkeleton"
import { Period } from "../../../../dashboards/dashboardTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { OrderType } from "../../../../payments/orderTypes"
import { StandardDashboardCard } from "../../components/StandardDashboardCard"
import { fetchPosAverageAmount, fetchTodaysPosSales, MerchantHomeQuery } from "../merchantHomeApi"

import { ErrorFallback } from "./PosSalesCards"

type TakeawaySalesCardProps = Readonly<{ merchantId: number }>

export function TodaysTakeawayOrders({ merchantId }: TakeawaySalesCardProps) {
    const moneyFactory = useMoney({})

    const todaysTakeawaySales = useApiQuery(fetchTodaysPosSales, {
        queryName: MerchantHomeQuery.TODAYS_TAKEAWAY_SALES,
        dependencies: {
            merchantId,
            orderType: OrderType.TAKE_AWAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: RefetchIntervals.medium,
    })

    return (
        <>
            <LocalizedStrict id="merchant-home-takeaway-number-of-orders" attrs={{ title: true, titleTooltip: true }}>
                <StandardDashboardCard
                    variant="number"
                    title="Orders"
                    titleTooltip="Number of takeaway orders for today"
                    data={todaysTakeawaySales.data?.posSale.numberOfOrders}
                />
            </LocalizedStrict>
            {todaysTakeawaySales.data && (
                <LocalizedStrict id="merchant-home-takeaway-turnover" attrs={{ title: true, titleTooltip: true }}>
                    <StandardDashboardCard
                        variant="number"
                        title="Turnover"
                        titleTooltip="Turnover tooltip"
                        subtitle={todaysTakeawaySales.data.posSale.totalAmount.currency}
                        data={moneyFactory.formatAsNumberWithScale(
                            toDinero(todaysTakeawaySales.data.posSale.totalAmount),
                            "value",
                        )}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}

export function TakeawayAverageAmount({ merchantId }: TakeawaySalesCardProps) {
    const moneyFactory = useMoney({})

    const takeawayAverageAmountResult = useApiQuery(fetchPosAverageAmount, {
        queryName: MerchantHomeQuery.TAKEAWAY_AVERAGE_AMOUNT,
        dependencies: {
            merchantId,
            period: Period.TODAY,
            orderType: OrderType.TAKE_AWAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: RefetchIntervals.medium,
    })

    const totalAmount = toDineroOrZero(takeawayAverageAmountResult.data?.posSales[0]?.salesSummary.totalAmount)

    return (
        <LocalizedStrict
            id="merchant-home-takeaway-avg-amount"
            attrs={{ title: true, titleTooltip: true, subtitle: true }}
        >
            <StandardDashboardCard
                variant="number"
                title="Avg. amount"
                titleTooltip="Avg. amount tooltip"
                subtitle="DKK"
                data={moneyFactory.formatAsNumber(totalAmount)}
            />
        </LocalizedStrict>
    )
}

export function TakeawaySalesCards() {
    const { merchantId } = useMerchant()

    return (
        <>
            <QueryErrorBoundary
                Fallback={(props) => (
                    <>
                        <LocalizedStrict id="merchant-home-takeaway-number-of-orders" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="Orders" />
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-home-takeaway-turnover" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="Turnover" />
                        </LocalizedStrict>
                    </>
                )}
            >
                <Suspense fallback={<DashboardCardSkeleton count={2} />}>
                    <TodaysTakeawayOrders merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>

            <QueryErrorBoundary
                Fallback={(props) => (
                    <LocalizedStrict id="merchant-home-takeaway-avg-amount" attrs={{ title: true }}>
                        <ErrorFallback {...props} title="Avg. amount" />
                    </LocalizedStrict>
                )}
            >
                <Suspense fallback={<DashboardCardSkeleton />}>
                    <TakeawayAverageAmount merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
